import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  type TableColumnsType,
} from "antd";
import styles from "../MaterialFormFields.module.less";
import { PriceCell } from "../PriceCell";
import { useFormEditable } from "../../../hooks/useFormEditable";
import { useModalBehavior } from "@/hooks/behaviors";
import type { LowValuePurchaseRequisitionFormValue } from "../../../type";
import {
  useCostCenterOptions,
  useGLAccountOptions,
  useMaterialGroupOptions,
} from "@/hooks/services/useSelectOptionsQuery";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export function useNoMaterialCodeColumns() {
  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");

  const {
    isMainFormDisabled,
    isPurchasingStaffFormFieldsDisabled,
    isPurchaseManagerAuditFormFieldsDisabled,
    isFinancialFirstAuditFormFieldsDisabled,
  } = useFormEditable();

  const shouldDisabled =
    isPurchaseManagerAuditFormFieldsDisabled &&
    isPurchasingStaffFormFieldsDisabled;

  const form = Form.useFormInstance<LowValuePurchaseRequisitionFormValue>();

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const {
    open: pricePopupOpen,
    openModal: openPricePopup,
    closeModal: closePricePopup,
  } = useModalBehavior();

  const factoryValue = Form.useWatch("factoryValue", form);
  const costCenterOptions = useCostCenterOptions(factoryValue!);
  const materialGroupOptions = useMaterialGroupOptions();
  const glAccountOptions = useGLAccountOptions(factoryValue!);

  const handlePriceSave = (values: {
    price: number;
    priceUnit: string;
    priceHkd: number;
  }) => {
    if (editingIndex !== null) {
      form.setFieldValue(["itemList", editingIndex, "price"], values.price);
      form.setFieldValue(
        ["itemList", editingIndex, "priceUnit"],
        values.priceUnit,
      );
      form.setFieldValue(
        ["itemList", editingIndex, "priceHkd"],
        values.priceHkd,
      );
      // 每次 priceHkd 变化时都重新计算 applyAmtHkd
      const priceHkd = values.priceHkd;
      const applyQty = form.getFieldValue([
        "itemList",
        editingIndex,
        "applyQty",
      ]);
      if (applyQty && priceHkd) {
        form.setFieldValue(
          ["itemList", editingIndex, "applyAmtHkd"],
          (priceHkd * applyQty).toFixed(2),
        );
      }
    }
    setEditingIndex(null);
  };

  const handleQtyChange = (index: number, applyQty: number) => {
    // 每次 applyQty 变化时都重新计算 applyAmtHkd
    const priceHkd = form.getFieldValue(["itemList", index, "priceHkd"]);
    if (priceHkd) {
      form.setFieldValue(
        ["itemList", index, "applyAmtHkd"],
        (priceHkd * applyQty).toFixed(2),
      );
    }
  };

  const columns: TableColumnsType = [
    {
      title: <span className={styles.required}>{t("描述")}</span>,
      dataIndex: "description",
      width: 150,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "description"]}
          rules={[
            {
              required: true,
              message: t("请输入描述"),
            },
          ]}
          noStyle
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("物料组")}</span>,
      dataIndex: "materialGroup",
      width: 220,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "materialGroup"]}
          rules={[
            {
              required: true,
              message: t("请选择物料组"),
            },
          ]}
          noStyle
        >
          <Select
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder={t("请选择")}
            options={materialGroupOptions}
            allowClear
          />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("成本中心")}</span>,
      dataIndex: "costCenterNo",
      width: 220,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "costCenterNo"]}
          rules={[{ required: true, message: t("请选择") }]}
          noStyle
        >
          <Select
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder={t("请选择")}
            options={costCenterOptions}
            disabled={isFinancialFirstAuditFormFieldsDisabled}
          />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("会计科目")}</span>,
      dataIndex: "glAccount",
      width: 220,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "glAccount"]}
          rules={[
            {
              required: true,
              message: t("请输入会计科目"),
            },
          ]}
          noStyle
        >
          <Select
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            placeholder={t("请选择")}
            options={glAccountOptions}
            disabled={isFinancialFirstAuditFormFieldsDisabled}
          />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("要求到货日期")}</span>,
      dataIndex: "deliveryDate",
      width: 200,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "deliveryDate"]}
          rules={[
            {
              required: true,
              message: t("请选择日期"),
            },
          ]}
          noStyle
        >
          <DatePicker placeholder={t("请选择")} style={{ width: "100%" }} />
        </Form.Item>
      ),
    },
    {
      title: t("单价"),
      dataIndex: "priceHkd",
      width: 120,
      align: "center",
      render: (_, field) => (
        <PriceCell
          disabled={shouldDisabled}
          fieldName={field.name}
          editingIndex={editingIndex}
          pricePopupOpen={pricePopupOpen}
          onOpenEdit={(index) => {
            setEditingIndex(index);
            openPricePopup();
          }}
          onClose={() => {
            closePricePopup();
            setEditingIndex(null);
          }}
          onSave={handlePriceSave}
        />
      ),
    },
    {
      title: <span className={styles.required}>{t("申请数量")}</span>,
      dataIndex: "applyQty",
      width: 120,
      align: "center",
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyQty"]}
          rules={[
            {
              required: true,
              message: t("请输入申请数量"),
            },
          ]}
          noStyle
        >
          <InputNumber
            disabled={shouldDisabled}
            min={1}
            precision={0}
            placeholder={t("请输入")}
            onChange={(value) => {
              if (value !== null && value !== undefined) {
                handleQtyChange(field.name, value);
              }
            }}
          />
        </Form.Item>
      ),
    },
    {
      title: t("申请总金额"),
      dataIndex: "applyAmtHkd",
      align: "center",
      width: 120,
      render: (_, field) => {
        return (
          <Form.Item name={[field.name, "applyAmtHkd"]} noStyle>
            <InputNumber min={0} precision={2} placeholder={t("自动计算")} />
          </Form.Item>
        );
      },
    },
    {
      title: <span className={styles.required}>{t("采购说明")}</span>,
      dataIndex: "applyRemarks",
      width: 180,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyRemarks"]}
          rules={[
            {
              required: true,
              message: t("请输入采购说明"),
            },
          ]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: <span className={styles.required}>{t("采购原因")}</span>,
      dataIndex: "applyReason",
      width: 180,
      render: (_, field) => (
        <Form.Item
          name={[field.name, "applyReason"]}
          rules={[
            {
              required: true,
              message: t("请输入采购原因"),
            },
          ]}
          noStyle
        >
          <Input.TextArea rows={1} placeholder={t("请输入")} />
        </Form.Item>
      ),
    },
    {
      title: t("优先采购"),
      dataIndex: "highestPriority",
      width: 160,
      align: "center",
      render: (_, field) => (
        <Form.Item name={[field.name, "highestPriority"]} noStyle>
          <Radio.Group>
            <Radio value={1}>{tCommon("是")}</Radio>
            <Radio value={0}>{tCommon("否")}</Radio>
          </Radio.Group>
        </Form.Item>
      ),
    },
    {
      title: t("上传附件"),
      dataIndex: "attachments",
      width: 500,
      render: (_, field) => (
        <div style={{ marginBottom: -24 }}>
          <MaterialFileFormFields
            parentFieldName="itemList"
            fieldName={[field.name, "attachments"]}
            label=""
            maxCount={5}
            required={false}
            hiddenRemove={isMainFormDisabled}
          />
        </div>
      ),
    },
  ];

  return columns;
}
